import { useEffect, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  subscribeEntityAuditChanged,
  useApiClient,
  useModuleAvailability,
} from "@playbookai/api-clients";
import { reportingPeriodLabel } from "@playbookai/types";
import type { KpiTileRange } from "@playbookai/widget-schemas";
import {
  fetchKpiSource,
  kpiSourceKey,
  KpiSourceForbidden,
  KpiSourceUnavailable,
  type ResolvedKpiSource,
} from "./kpi-source.js";

/**
 * One KPI, resolved for a widget that displays it.
 *
 * The outcome is an explicit STATUS rather than a raw query result because the
 * widget has to distinguish "you may not see this", "this is gone" and "this has
 * no reading yet" — three different sentences, and none of them a number.
 */
export type KpiSourceStatus = "loading" | "ready" | "unavailable" | "forbidden";

export interface UseKpiSourceResult {
  readonly status: KpiSourceStatus;
  readonly kpi: ResolvedKpiSource | null;
  /** The reporting period the latest reading falls in, per KPI Manager. */
  readonly periodLabel: string | null;
  readonly message: string | null;
  readonly refetch: () => void;
  /**
   * False when a retry cannot help: KPI Manager is off for this workspace
   * (ADR-0100), so nothing was asked and asking again changes nothing.
   */
  readonly retryable: boolean;
}

const noSubscribe = (): (() => void) => () => undefined;
const noScope = (): null => null;

/** Isolate tile and picker caches across tenant, principal and session changes. */
export function useKpiCacheScope(): readonly unknown[] {
  const config = useApiClient();
  const source = config.authScope;
  const scope = useSyncExternalStore(
    source?.subscribe ?? noSubscribe,
    source?.getSnapshot ?? noScope,
    source?.getSnapshot ?? noScope,
  );
  return [
    scope?.tenantId ?? null,
    scope?.actorUserId ?? config.getCurrentUser?.()?.id ?? null,
    scope?.authSessionGeneration ?? null,
  ];
}

export function useKpiSource(
  kpiId: string,
  range: KpiTileRange,
): UseKpiSourceResult {
  const config = useApiClient();
  const scope = useKpiCacheScope();
  // KPI Manager off (ADR-0100): no read at all, and the one sentence instead
  // of a value, so an existing widget stays on the page and says why.
  const kpiOff = useModuleAvailability().unavailableMessage("kpi");
  const queryClient = useQueryClient();
  const enabled = kpiId !== "" && kpiOff === null;
  useEffect(() => {
    if (!enabled) return;
    return subscribeEntityAuditChanged(
      () => {
        // Cancel even an initial read; its response may predate the audit event.
        void queryClient
          .cancelQueries({ queryKey: ["kpi-source"], type: "active" })
          .then(() =>
            queryClient.invalidateQueries(
              {
                queryKey: ["kpi-source"],
                type: "active",
                refetchType: "active",
              },
              { cancelRefetch: true },
            ),
          );
      },
      { scope: queryClient, key: "kpi-source" },
    );
  }, [enabled, queryClient]);
  const query = useQuery<ResolvedKpiSource>({
    queryKey: kpiSourceKey(kpiId, range, scope),
    queryFn: ({ signal }) => fetchKpiSource(config, kpiId, range, signal),
    enabled,
    staleTime: 30_000,
    // A refused read is a fact, not a flake: retrying a 403 five times delays
    // the honest message and puts four more denials in the audit log. The
    // access-revocation path depends on this being immediate.
    retry: (failureCount, error) =>
      error instanceof KpiSourceForbidden ||
      error instanceof KpiSourceUnavailable
        ? false
        : failureCount < 2,
  });

  if (kpiOff !== null) {
    return {
      status: "unavailable",
      kpi: null,
      periodLabel: null,
      message: kpiOff,
      refetch: () => undefined,
      retryable: false,
    };
  }

  const kpi = query.data ?? null;
  const status: KpiSourceStatus =
    query.error instanceof KpiSourceForbidden
      ? "forbidden"
      : query.error
        ? "unavailable"
        : kpi
          ? "ready"
          : "loading";

  return {
    status,
    // A revoked reader must not keep looking at what they could see a moment
    // ago: TanStack keeps `data` through a failed refetch, so the KPI is
    // dropped explicitly whenever the latest read did not succeed.
    kpi: status === "ready" ? kpi : null,
    periodLabel:
      kpi?.observedAt == null
        ? null
        : reportingPeriodLabel(
            kpi.observedAt,
            // The wire value is the shared `KpiReportingFrequency` vocabulary.
            kpi.reportingFrequency as Parameters<
              typeof reportingPeriodLabel
            >[1],
          ),
    message: query.error instanceof Error ? query.error.message : null,
    refetch: () => void query.refetch(),
    retryable: true,
  };
}
